import { fmtDate } from '../../lib/formatDate';
import { ONBOARDING_IDENTITY_PROVIDED_KEYS, resolveTrustedContact, withLegacyEmployerTitle } from '@steward-app/steward-types';

/**
 * What the "Review and send" page shows (crossmarc/steward-app#1888): the client's own answers,
 * grouped by page, read from the form's `values` state. No fetch and no new storage.
 *
 * Only pages the caller passes in appear, so a section the firm did not ask for (#1882) never
 * does. The Social Security number is never in `values`; the page shows the last four digits the
 * server returned when the client saved it (`taxIdHint`), and nothing else.
 */
export type Values = Record<string, string | boolean | string[]>;

/** A page of the form: the two stored sections `spouse` and `children` share `family`. */
export type Page = 'client' | 'requests' | 'family' | 'address' | 'trustedContact' | 'beneficiary' | 'about' | 'review';
/** Every page that holds the client's answers. `requests` (steward-app#2284) is one, but it is
 *  not drawn by {@link reviewGroup}: its files and typed answers are not form fields. */
export type AnswerPage = Exclude<Page, 'review'>;
export type ReviewPage = Exclude<AnswerPage, 'requests'>;

export interface ReviewRow { label: string; value: string | null }
export interface ReviewGroup {
  page: ReviewPage;
  rows: ReviewRow[];
  /** An optional page with nothing in it: shown as "Nothing added" with an Add link. */
  empty: boolean;
}

export const NOT_FILLED_IN = 'Not filled in';

const str = (v: Values, k: string): string => {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
};
const list = (v: Values, k: string): string[] => {
  const x = v[k];
  return Array.isArray(x) ? x.map((s) => (typeof s === 'string' ? s.trim() : '')) : [];
};
const orNull = (s: string): string | null => (s === '' ? null : s);

export interface Who { hints: Partial<Record<'client' | 'spouse', string>>; invitedEmail: string }

const ssn = (hint: string | undefined): string | null => (hint ? `•••-••-${hint}` : null);

function personRows(raw: Values, s: 'client' | 'spouse', who: Who, nameLabel: string): ReviewRow[] {
  const v = withLegacyEmployerTitle(raw);
  const name = [str(v, `${s}_firstName`), str(v, `${s}_middleName`), str(v, `${s}_lastName`)].filter(Boolean).join(' ');
  return [
    { label: nameLabel, value: orNull(name) },
    { label: 'Date of birth', value: orNull(fmtDay(str(v, `${s}_dateOfBirth`))) },
    { label: 'Social Security number', value: ssn(who.hints[s]) },
    { label: 'Employer', value: orNull(str(v, `${s}_employer`)) },
    { label: 'Title', value: orNull(str(v, `${s}_title`)) },
    { label: 'Phone', value: orNull(str(v, `${s}_phone`)) },
    { label: 'Email', value: orNull(s === 'client' ? who.invitedEmail.trim() || str(v, 'client_email') : str(v, `${s}_email`)) },
  ];
}

const oneLine = (v: Values, prefix: string): string => {
  const street = str(v, `address_${prefix}Street`);
  const city = str(v, `address_${prefix}City`);
  const state = str(v, `address_${prefix}State`);
  const zip = str(v, `address_${prefix}PostalCode`);
  return [street, [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ');
};

/** A date-only answer as "Jun 2, 1984" through the app's one date formatter (it pins date-only
 *  values to UTC, so the day never shifts); blank stays blank and unparseable text passes through. */
const fmtDay = (d: string): string => (d ? fmtDate(d) : d);

/** "Ellie, born Mar 2, 2014. Sam." for the two index-aligned children lists. */
export function childrenSummary(v: Values): string | null {
  const names = list(v, 'children_names');
  const dobs = list(v, 'children_dateOfBirths');
  const parts: string[] = [];
  for (let i = 0; i < Math.max(names.length, dobs.length); i++) {
    const n = names[i] ?? '';
    const d = dobs[i] ?? '';
    if (n && d) parts.push(`${n}, born ${fmtDay(d)}.`);
    else if (n) parts.push(`${n}.`);
    else if (d) parts.push(`Born ${fmtDay(d)}.`);
  }
  return parts.length ? parts.join(' ') : null;
}

export function reviewGroup(page: ReviewPage, v: Values, who: Who, opts: { spouse: boolean; children: boolean }): ReviewGroup {
  switch (page) {
    case 'client':
      return { page, rows: personRows(v, 'client', who, 'Legal name'), empty: false };
    case 'family': {
      const rows: ReviewRow[] = [];
      if (opts.spouse) {
        const included = v['spouse_included'];
        if (included === true) rows.push(...personRows(v, 'spouse', who, 'Spouse or partner'));
        else rows.push({ label: 'Spouse or partner', value: included === false ? 'None' : null });
      }
      if (opts.children) rows.push({ label: 'Children', value: childrenSummary(v) });
      return { page, rows, empty: false };
    }
    case 'address': {
      const rows: ReviewRow[] = [{ label: 'Legal address', value: orNull(oneLine(v, 'legal')) }];
      rows.push({
        label: 'Mailing address',
        value: v['address_mailingSameAsLegal'] === true ? 'Same as legal address' : orNull(oneLine(v, 'mailing')),
      });
      if (opts.spouse && v['spouse_included'] === true) {
        const first = str(v, 'spouse_firstName');
        const label = first ? `${first}'s address` : 'Spouse or partner’s address';
        rows.push({
          label,
          value: v['address_spouseSameAsClient'] !== false ? 'Same as yours' : orNull(oneLine(v, 'spouseLegal')),
        });
      }
      return { page, rows, empty: false };
    }
    case 'trustedContact': {
      const r = resolveTrustedContact(v);
      return {
        page, empty: false,
        rows: [
          { label: 'Name', value: orNull(r?.name ?? '') },
          { label: 'Relationship', value: orNull(r?.relationship ?? '') },
          { label: 'Phone', value: orNull(r?.phone ?? '') },
          { label: 'Email', value: orNull(r?.email ?? '') },
        ],
      };
    }
    case 'beneficiary':
      return {
        page, empty: false,
        rows: [
          { label: 'Primary beneficiaries', value: orNull(str(v, 'beneficiary_primary')) },
          { label: 'Contingent beneficiaries', value: orNull(str(v, 'beneficiary_contingent')) },
          { label: 'Anything else about your wishes', value: orNull(str(v, 'beneficiary_notes')) },
        ],
      };
    case 'about': {
      // Optional as a whole: only what was answered, or "Nothing added".
      const rows = ABOUT_LABELS.map(([key, label]) => ({ label, value: orNull(str(v, key)) })).filter((r) => r.value !== null);
      return { page, rows, empty: rows.length === 0 };
    }
  }
}

const ABOUT_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['about_anniversary', 'Anniversary'], ['about_dreams', 'Dreams'], ['about_favoriteMedia', 'Favorite books, shows and music'],
  ['about_hobbies', 'Hobbies'], ['about_sportsPlayed', 'Sports you play'], ['about_favoriteTeams', 'Favorite teams'],
  ['about_foodAndDrink', 'Food and drink'], ['about_allergies', 'Allergies'], ['about_almaMater', 'Alma mater'],
  ['about_milestones', 'Milestones coming up'], ['about_dislikes', 'Dislikes'],
];

/**
 * What a page still needs, in words, for the "not finished" note. Mirrors the required keys of
 * `onboardingSectionStatus` (`../onboardingFormStatus.ts`); a test pins that this list is empty
 * exactly when that function says `complete`. `answers` is the same map that function takes
 * (values plus the identity-provided flags).
 */
export function missingFor(section: 'client' | 'spouse' | 'address' | 'trustedContact' | 'beneficiary', a: Record<string, unknown>): string[] {
  const has = (k: string): boolean => {
    const x = a[k];
    return typeof x === 'string' ? x.trim() !== '' : typeof x === 'boolean' ? true : false;
  };
  const out: string[] = [];
  const person = (s: 'client' | 'spouse', who: string) => {
    if (!has(`${s}_firstName`)) out.push(`${who} legal first name`);
    if (!has(`${s}_lastName`)) out.push(`${who} legal last name`);
    if (!has(`${s}_dateOfBirth`)) out.push(`${who} date of birth`);
    if (a[ONBOARDING_IDENTITY_PROVIDED_KEYS[s]] !== true) out.push(`${who} Social Security number`);
  };
  const addr = (prefix: string) => ['Street', 'City', 'State', 'PostalCode'].every((p) => has(`address_${prefix}${p}`));
  switch (section) {
    case 'client': person('client', 'your'); break;
    case 'spouse':
      if (typeof a['spouse_included'] !== 'boolean') out.push('whether to include a spouse or partner');
      else if (a['spouse_included'] === true) person('spouse', 'their');
      break;
    case 'address':
      if (!addr('legal')) out.push('your full legal address');
      if (a['address_mailingSameAsLegal'] !== true && !addr('mailing')) out.push('your full mailing address');
      if (a['spouse_included'] === true && a['address_spouseSameAsClient'] === false && !addr('spouseLegal')) {
        out.push('your spouse or partner’s full address');
      }
      break;
    case 'trustedContact': {
      const r = resolveTrustedContact(a);
      if (!r || !r.name) out.push('a name');
      if (!r || (!r.phone && !r.email)) {
        // A spouse's phone and email are kept on the Your family page, so say where to add them.
        out.push(r?.subject === 'spouse' ? 'a phone number or email (on the Your family page)' : 'a phone number or email');
      }
      break;
    }
    case 'beneficiary':
      if (!has('beneficiary_primary')) out.push('your primary beneficiaries');
      break;
  }
  return out;
}

/** The entry `missingFor` writes for a spouse or partner's missing Social Security number. */
export const SPOUSE_SSN_MISSING = 'their Social Security number';

/** "Sam still needs a Social Security number." / "Sam and Rae still need Social Security numbers." */
export function ssnNeededSentence(names: string[]): string {
  const list = names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return names.length === 1 ? `${list} still needs a Social Security number.` : `${list} still need Social Security numbers.`;
}
